import React from "react";
import { Share2, Copy, MessageCircle, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Friends & Family share card — admin-only panel that produces a ready-to-paste
 * personal invite + one-tap WhatsApp / Messenger / Copy buttons.
 *
 * Purpose: squeeze one more wave of pre-launch signups from Anthony's
 * personal network (the warmest possible audience). Each share is the
 * camp-compass.uk/register URL with a friendly hand-written caption so
 * it never reads as bulk marketing.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const SIGNUP_URL_WA = `${PUBLIC_URL}/register?ref=fnf-wa`;
const SIGNUP_URL_FB = `${PUBLIC_URL}/register?ref=fnf-fb`;
const SIGNUP_URL_COPY = `${PUBLIC_URL}/register?ref=fnf-copy`;

const buildMessage = (url) => `Hey! 🚀

CampCompass UK is LIVE today — the biggest UK campsite map ever built (22,000+ sites), free to browse.

Everyone signing up today gets 14 days of Pro on us. Sign up in 20 seconds: ${url}

If you can share with one camping mate I'd be over the moon 🏕

— Anthony`;

function copy(text, label = "Message") {
  try {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error("Couldn't copy — long-press to select manually");
  }
}

export default function FriendsFamilyShareCard() {
  const waMessage = buildMessage(SIGNUP_URL_WA);
  const fbMessage = buildMessage(SIGNUP_URL_FB);
  const copyMessage = buildMessage(SIGNUP_URL_COPY);
  const waHref = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SIGNUP_URL_FB)}&quote=${encodeURIComponent(fbMessage)}`;

  return (
    <section
      className="border-2 hairline-strong bg-white p-6 mb-10"
      data-testid="admin-friends-family-share"
    >
      <header className="mb-4">
        <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Warmest audience first
        </div>
        <h2 className="font-display text-2xl mt-1 flex items-center gap-2">
          <Share2 className="w-5 h-5" strokeWidth={1.7} /> Friends &amp; family blast
        </h2>
        <p className="text-sm text-[#555] mt-1.5 max-w-2xl leading-relaxed">
          One-tap share for your personal contacts. The message is pre-written, friendly,
          and includes the direct signup link. Use it on WhatsApp groups, Messenger family
          chats, or copy and paste into iMessage / Signal.
        </p>
      </header>

      {/* The message preview */}
      <pre
        className="bg-[#fafafa] border hairline p-4 text-sm leading-relaxed whitespace-pre-wrap font-sans mb-4"
        data-testid="ff-share-preview"
      >
        {copyMessage}
      </pre>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-bold mono uppercase tracking-[0.14em] transition-colors"
          data-testid="ff-share-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <a
          href={fbHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#1877f2] hover:bg-[#0c63d4] text-white text-sm font-bold mono uppercase tracking-[0.14em] transition-colors"
          data-testid="ff-share-messenger"
        >
          <Send className="w-4 h-4" /> Messenger / FB
        </a>
        <button
          type="button"
          onClick={() => copy(copyMessage)}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-black hover:bg-[#222] text-white text-sm font-bold mono uppercase tracking-[0.14em] transition-colors"
          data-testid="ff-share-copy-message"
        >
          <Copy className="w-4 h-4" /> Copy message
        </button>
        <button
          type="button"
          onClick={() => copy(SIGNUP_URL_COPY, "Signup link")}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 border hairline-strong hover:bg-[#fafafa] text-[#111] text-sm font-bold mono uppercase tracking-[0.14em] transition-colors"
          data-testid="ff-share-copy-link"
        >
          <Copy className="w-4 h-4" /> Copy link only
        </button>
      </div>

      <p className="mono text-[10px] text-[#888] mt-4 leading-snug border-t hairline pt-3">
        Tip: post 5-10 personal messages in one sitting then leave it for a day — bulk
        sending to the same Messenger account too fast can trigger their spam filter.
      </p>
    </section>
  );
}
